import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { FetchError, rewordValidationMessages, ValidationError, type ErrorDetails } from '#api/errors.ts';
import { SPR } from '#util/validation/zod-utils.ts';

export const ORG_ID_FIELD_ID = '19714581424404';
export const REASON_FIELD_ID = '19725850252692';

export function useContactUsMutation() {
  return useMutation({
    mutationFn: createTicket,
  });
}

async function createTicket(requestBody: string) {
  // https://developer.zendesk.com/api-reference/ticketing/tickets/ticket-requests
  const response = await fetch('https://definednetworking.zendesk.com/api/v2/requests.json', {
    method: 'POST',
    body: requestBody,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (response.ok) {
    return response.json();
  }

  return await convertZendeskError(response).catch((err: unknown) => {
    rewordValidationMessages(err, (details) => {
      if (details.path === 'email') {
        return 'Email is not properly formatted';
      }
    });
  });
}

/**
 * Takes a response that is not ok, and tries to throw a standard ValidationError
 * so that it can be handled by our forms
 */
async function convertZendeskError(response: Response) {
  // Clone the response before we consume the json, so devtools can show it too
  const clonedResponse = response.clone();

  if (response.status === 422) {
    const body = await clonedResponse.json().catch(() => {
      throw new FetchError(response);
    });

    const { data: parsedBody, success } = SPR(ZendeskErrorSchema.safeParse(body));

    if (success && parsedBody.error === 'RecordInvalid') {
      const errors = Object.keys(parsedBody.details).map((field) => {
        const message = parsedBody.details[field]?.[0]?.description ?? 'Invalid value';
        return {
          path: field === 'requester' && message.startsWith('Requester: Email:') ? 'email' : field,
          code: 'ERR_ZENDESK_VALIDATION',
          message,
        };
      }) satisfies ErrorDetails[];

      throw new ValidationError(errors, response);
    }
  }

  throw new FetchError(response);
}

const ZendeskErrorSchema = z.object({
  error: z.string(),
  description: z.string(),
  details: z.record(z.array(z.object({ description: z.string() }))),
});
